import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductCardGrid from "@stoneage/App/Components/ProductCards/ProductCardGrid";
import ProductCardNarrow from "@stoneage/App/Components/ProductCards/ProductCardNarrow";
import { xScreen } from "@stoneage/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    extendedStyles?: ProductCarouselProductFullStyles;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductFullStyles {
    hiddenBelow?: InjectableCss;
    hiddenAbove?: InjectableCss;
}

export const productCarouselProductFullStyles: ProductCarouselProductFullStyles = {
    hiddenBelow: {
        css: css`
            display: flex;
            flex: 1;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    hiddenAbove: {
        css: css`
            display: none;
            ${xScreen("lte", "md")} {
                display: flex;
                flex: 1;
            }
        `,
    },
};

const ProductCarouselProductFull = ({
    carouselId,
    product,
    extendedStyles,
    productInfo,
    productSettings,
    updateProductInfo,
    actionsTemplate,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductFullStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.hiddenBelow}>
                <ProductCardGrid
                    product={product}
                    productInfo={productInfo}
                    showImage={true}
                    showCompare={false}
                    showAttributes={true}
                    showAvailability={true}
                    showPartNumbers={true}
                    showTitle={true}
                    showPrice={true}
                    showAddToCart={true}
                    isAuthenticated={true}
                    view="Grid"
                />
            </StyledWrapper>
            <StyledWrapper {...styles.hiddenAbove}>
                <ProductCardNarrow
                    showImage={true}
                    showCompare={false}
                    showAttributes={true}
                    showAvailability={true}
                    showPartNumbers={true}
                    showTitle={true}
                    showPrice={true}
                    showAddToCart={true}
                />
            </StyledWrapper>
            {actionsTemplate ? actionsTemplate(product) : []}
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductFull);
